import { ApiError } from "./api";
import { t, type StringKey } from "./i18n";

/** micro-unități → € (1 micro = 1e-6). Sub un cent afișăm mai multe zecimale. */
export function eur(micro: number, decimals?: number): string {
  const v = micro / 1_000_000;
  const d = decimals ?? (Math.abs(v) > 0 && Math.abs(v) < 0.01 ? 4 : 2);
  return `${v.toFixed(d)} €`;
}

export function num(n: number): string {
  return n.toLocaleString("en-US");
}

export function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Durata unei sesiuni (ms) → text scurt: „45s", „3m 20s", „1h 5m". */
export function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s <= 0) return "< 1s";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rem = s % 60;
    return rem ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
}

export function initials(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

export function pct(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

export function errText(err: unknown): string {
  if (err instanceof ApiError) {
    const data = err.data as { error?: string } | null;
    return data?.error ? `${t("err.generic")}: ${data.error}` : `${t("err.generic")} (${err.status})`;
  }
  return t("err.network");
}

/**
 * Erorile de facturare, traduse pentru client.
 *
 * `errText()` afișează codul mașină brut („Error: billing_not_configured") -
 * acceptabil în consola de admin, dar nu pe ecranul unde omul tocmai a dat click
 * pe „Choose Growth". Codurile vin din services/stripeBilling.ts prin
 * routes/billing.ts; cele care nu sunt aici cad pe mesajul generic, iar codul
 * rămâne în consolă pentru diagnostic.
 */
export function billingErrText(err: unknown): string {
  if (!(err instanceof ApiError)) return t("err.network");
  const data = err.data as { error?: string; minQuantity?: number } | null;
  const code = data?.error;
  if (!code) return t("errb.generic");
  // `sites_in_use` e singurul care poartă un număr: câte sloturi sunt ocupate
  // chiar acum. Fără el mesajul ar fi „nu poți reduce" fără să spună până unde.
  if (code === "sites_in_use" && typeof data?.minQuantity === "number") {
    return t("errb.sites_in_use", data.minQuantity);
  }
  const key = BILLING_ERROR_KEYS[code];
  if (key) return t(key);
  console.warn(`[billing] cod de eroare fără traducere: ${code} (${err.status})`);
  return t("errb.generic");
}

/**
 * Cod backend → cheie i18n. Explicit, nu construit din șablon, ca TypeScript să
 * verifice că fiecare cheie chiar există în dicționar - un `errb.${code}` ar
 * trece de compilator și ar afișa cheia crudă în producție.
 */
const BILLING_ERROR_KEYS: Partial<Record<string, StringKey>> = {
  billing_not_configured: "errb.billing_not_configured",
  stripe_error: "errb.stripe_error",
  already_subscribed: "errb.already_subscribed",
  no_stripe_customer: "errb.no_stripe_customer",
  no_subscription: "errb.no_subscription",
  same_plan: "errb.same_plan",
  same_quantity: "errb.same_quantity",
  not_activated: "errb.not_activated",
  plan_granted: "errb.plan_granted",
  not_cancelling: "errb.not_cancelling",
  subscription_ending: "errb.subscription_ending",
  sites_in_use: "errb.sites_in_use_generic",
  "invalid request": "errb.invalid_request",
};

// --- Fus orar Atlantic/Canary (Content Hub, programarea publicării) ----------
// Decizie de produs: programarea se afișează și se interpretează MEREU în ora
// din Tenerife (Atlantic/Canary, WET/WEST cu DST), niciodată „ora locală a
// browserului" - altfel un admin dintr-un alt fus ar programa în orbe.

const CANARY_TZ = "Atlantic/Canary";

/** Offsetul (ms) al fusului la un instant dat: utc_al_pereților − timp_real. */
function tzOffsetMs(date: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const m: Record<string, number> = {};
  for (const p of parts) if (p.type !== "literal") m[p.type] = Number(p.value);
  // ora din tz, citită ca și cum ar fi UTC
  const asUtc = Date.UTC(m.year, m.month - 1, m.day, m.hour === 24 ? 0 : m.hour, m.minute, m.second);
  return asUtc - date.getTime();
}

/**
 * String de la <input type="datetime-local"> ("2026-07-28T15:00"), interpretat
 * ca oră din Tenerife → epoch ms UTC. O singură iterație de offset e suficientă
 * pentru Canare (salturile DST sunt de o oră, nu la granița căutată aici).
 */
export function canaryLocalToEpoch(localStr: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localStr);
  if (!m) return null;
  const [, Y, Mo, D, H, Mi] = m.map(Number);
  const guess = Date.UTC(Y, Mo - 1, D, H, Mi);
  const offset = tzOffsetMs(new Date(guess), CANARY_TZ);
  return guess - offset;
}

/** epoch ms → valoarea pentru <input type="datetime-local">, în ora din Tenerife. */
export function epochToCanaryLocal(ms: number): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CANARY_TZ, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(new Date(ms));
  const m: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") m[p.type] = p.value;
  const hh = m.hour === "24" ? "00" : m.hour;
  return `${m.year}-${m.month}-${m.day}T${hh}:${m.minute}`;
}

/** epoch ms → text lizibil în ora din Tenerife, cu eticheta fusului. */
export function fmtCanary(ms: number): string {
  const s = new Intl.DateTimeFormat("en-GB", {
    timeZone: CANARY_TZ, day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(ms));
  return `${s} (Atlantic/Canary)`;
}

/**
 * Rândul „Renews on / Ends on" de pe cardul planului curent. Un abonament
 * anulat la finalul perioadei (din portalul Stripe sau prin ștergerea contului)
 * nu se reînnoiește: a spune „Renews on" despre el înseamnă să-i spui clientului
 * că va fi taxat. null = nimic de arătat (fără abonament Stripe sau fără scadență).
 */
export function renewalLine(plan: {
  hasStripeSubscription: boolean;
  renewsAt: number | null;
  cancelAtPeriodEnd: boolean;
}): string | null {
  if (!plan.hasStripeSubscription || !plan.renewsAt) return null;
  return plan.cancelAtPeriodEnd ? t("plan.endsOn", fmtDate(plan.renewsAt)) : t("plan.renewsOn", fmtDate(plan.renewsAt));
}

/** „1 visit" / „3 visits" - badge-ul din Conversations numără vizite, nu întrebări. */
export function visitsLabel(n: number): string {
  return n === 1 ? t("conv.countOne") : t("conv.count", num(n));
}

/** „1 question" / „3 questions" - banda de sumar numără întrebări (rândurile din conversations). */
export function questionsLabel(n: number): string {
  return n === 1 ? t("conv.sum.monthValOne") : t("conv.sum.monthVal", num(n));
}

/**
 * Erorile formularului „New site", traduse. `dev_origin` (400) și
 * `plan_site_limit` (403) veneau înainte ca „Error: dev_origin" brut. Null dacă
 * eroarea e altceva - apelantul cade pe celelalte traduceri / errText.
 * (`origin_taken` și `invalid request` au traducerile lor în main.tsx, pentru că
 * poartă nume de site.)
 */
export function siteCreateErrText(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const code = (err.data as { error?: string } | null)?.error;
  if (code === "dev_origin") return t("sites.devOrigin");
  if (code === "plan_site_limit") return t("sites.siteLimit");
  return null;
}
